import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { APP_NAME } from '../constants';
import { 
  getCurrentUser, 
  updateUserProfile,
  resendOtp,
  User 
} from '../services/authService';
import { uploadProfilePicture } from '../services/storageService';
import Button from '../components/Button';
import Input from '../components/Input';
import TopBar from '../components/TopBar';
import defaultProfileImage from '../assets/default-profile.jpg';
import PhoneInput from '../components/PhoneInput';


const Container = styled.div`
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
`;

const Breadcrumbs = styled.div`
  display: flex;
  margin-bottom: 20px;
  font-size: 14px;
  color: #555;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  margin: 0 8px;
`;

const ProfileSection = styled.div`
  margin-bottom: 40px;
  width: 100%;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  width: 100%;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 30px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;

const ProfileImageContainer = styled.div`
  flex-shrink: 0;
`;

const ProfileImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  
  &::after {
    content: 'Change Photo';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 5px;
    text-align: center;
    opacity: 0;
    transition: opacity 0.3s;
  }
  
  &:hover::after {
    opacity: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NameContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const AccountInfoContainer = styled.div`
  margin-top: 20px;
`;

const ButtonContainer = styled.div`
  margin-top: 30px;
  display: flex;
  // justify-content: space-between;
  justify-content: flex-end;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const SuccessMessage = styled.p`
  color: #43a047;
  margin-top: 6px;
  font-size: 14px;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: black;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    text-decoration: underline;
  }
  
  svg {
    margin-left: 6px;
  }
`;

const ResendButton = styled(Button)`
  background-color: transparent;
  color: #1976d2;
  padding: 0;
  min-width: 0;
  height: auto;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:disabled {
    color: #aaa;
    cursor: not-allowed;
  }
`;

const CooldownText = styled.span`
  font-size: 14px;
  color: #999;
`;

const ProfileScreen: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [sendingVerification, setSendingVerification] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    return () => { 
      if (timerRef.current) clearInterval(timerRef.current); 
    };
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const userData = await getCurrentUser();
        console.log('ProfileScreen - userData from getCurrentUser:', userData);
        
        if (userData) {
          setUser(userData);
          setFirstName(userData.firstName || '');
          setLastName(userData.lastName || '');
          setEmail(userData.email);
          setPhoneNumber(userData.phoneNumber || '');
          setIsPhoneVerified(userData.phoneVerified || false);
          setProfileImageUrl(userData.profilePictureURL || '');
          
          console.log('ProfileScreen - set firstName:', userData.firstName);
          console.log('ProfileScreen - set lastName:', userData.lastName);
        } else {
          navigate('/login');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleProfileImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      setProfileImageFile(files[0]);
      setProfileImageUrl(URL.createObjectURL(files[0]));
    }
  };
  
  const startResendCooldownTimer = () => {
    setResendCooldown(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };
  
  const handleResendPhoneVerification = async () => {
    if (!user?.id || sendingVerification || resendCooldown > 0) return;
    
    setError('');
    setSuccess('');
    setSendingVerification(true);
    
    try {
      await resendOtp({ type: 'phone', userId: user.id });
      setSuccess('Verification link sent to your phone. Please click the link when you receive it.');
      startResendCooldownTimer();
    } catch (error: any) {
      console.error("Error sending phone verification link:", error);
      setError('Failed to send verification link. Please try again later.');
    } finally {
      setSendingVerification(false);
    }
  };

  const handleSaveProfile = async () => {
    setError('');
    setSuccess('');
    
    if (!user) return;

    if (!firstName.trim() || !lastName.trim()) {
      setError('First name and last name are required');
      return;
    }

    try {
      setSaving(true);

      let updatedProfileUrl = user.profilePictureURL || '';

      // Upload profile picture if changed
      if (profileImageFile) {
        updatedProfileUrl = await uploadProfilePicture(user.id, profileImageFile);
      }

      // Update user profile
      await updateUserProfile({
        userId: user.id,
        firstName,
        lastName,
        profilePictureURL: updatedProfileUrl,
      });

      setSuccess('Profile updated successfully');
      
      // Update the local user state with new values
      setUser({
        ...user,
        firstName,
        lastName,
        displayName: `${firstName} ${lastName}`,
        profilePictureURL: updatedProfileUrl,
      });

    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleNavigateToDashboard = () => {
    navigate('/dashboard');
  };
  
  const handleNavigateToChangePassword = () => {
    navigate('/change-password');
  };

  const handleResendVerification = async () => {
    setError('');
    setSuccess('');
    
    if (!user) return;

    try {
      setSendingVerification(true);
      
      // Call the actual API to send verification link
      await resendOtp({ type: 'phone', userId: user.id });
      
      setSuccess('Verification link sent to your phone. Please check your messages.');
      setSendingVerification(false);
      
      // Start cooldown timer (60 seconds)
      setResendCooldown(60);
      timerRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

    } catch (error: any) {
      console.error('Error sending verification:', error);
      setError(error?.message || 'Failed to send verification. Please try again.');
      setSendingVerification(false);
    }
  };

  if (loading) {
    return <Container>Loading...</Container>;  }

  return (
    <>
      <Helmet>
        <title>{APP_NAME} - Profile</title>
        <meta name="description" content={`${APP_NAME} - Profile`} />
      </Helmet>
      <Container>
      <ContentWrapper>
        <TopBar user={user} />
        
        <Breadcrumbs>
          <BreadcrumbLink onClick={handleNavigateToDashboard}>My Account</BreadcrumbLink>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          <span>My Profile</span>
        </Breadcrumbs>
        
        <ProfileSection>
          <SectionTitle>Personal Information</SectionTitle>
          
          <FormContainer>
            <ProfileHeader>
              <ProfileImageContainer>
                <ProfileImage onClick={handleProfileImageClick}>
                  <img 
                    src={profileImageUrl || defaultProfileImage} 
                    alt="Profile" 
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = defaultProfileImage;
                    }} 
                  />
                </ProfileImage>
                <HiddenFileInput 
                  type="file" 
                  accept="image/*" 
                  ref={fileInputRef} 
                  onChange={handleProfileImageChange} 
                />
              </ProfileImageContainer>
              
              <NameContainer>
                <Input 
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter your first name"
                  name="firstName"
                  label="First Name"
                  required
                  secondary
                  error={error && !firstName.trim() ? 'First name is required' : undefined}
                />
                
                <Input 
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  name="lastName"
                  label="Last Name"
                  required
                  secondary
                  error={error && !lastName.trim() ? 'Last name is required' : undefined}
                />
              </NameContainer>
            </ProfileHeader>

            <AccountInfoContainer>
              <Input 
                type="email"
                value={email}
                onChange={() => {}}
                placeholder=""
                name="email"
                label="Email Address"
                readOnly
                secondary
              />
              
              <PhoneInput 
                value={phoneNumber}
                onChange={() => {}}
                placeholder=""
                name="phoneNumber"
                label="Phone Number"
                disabled
                secondary
              />
              
              {phoneNumber && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    {isPhoneVerified ? (
                      <span style={{ color: '#4caf50', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px' }}>✓</span> Phone verified
                      </span>
                    ) : (
                      <span style={{ color: '#f44336', fontSize: '14px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px' }}>!</span> Phone not verified
                      </span>
                    )}
                  </div>
                  
                  {!isPhoneVerified && (
                    <button 
                      onClick={handleResendVerification}
                      disabled={sendingVerification || resendCooldown > 0}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: sendingVerification || resendCooldown > 0 ? '#999' : '#1976d2',
                        cursor: sendingVerification || resendCooldown > 0 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        textDecoration: 'underline',
                        padding: '5px 10px'
                      }}
                    >
                      {resendCooldown > 0 
                        ? `Resend link (${resendCooldown}s)` 
                        : sendingVerification 
                          ? 'Sending...' 
                          : 'Send verification link'}
                    </button>
                  )}
                </div>
              )}
            </AccountInfoContainer>
            
            {success && <SuccessMessage>{success}</SuccessMessage>}
            {error && <p style={{ color: '#f44336', marginTop: '6px', fontSize: '14px' }}>{error}</p>}

          </FormContainer>
          <ButtonContainer>
            <LinkButton onClick={handleNavigateToChangePassword}>
              Change Password
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 17L15 12L10 7" stroke="#1976d2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </LinkButton>
            <Button 
              text="Save" 
              onClick={handleSaveProfile} 
              loading={saving}
              bgColor="#000"
              textColor="#fff"
              width="auto"
              minWidth="150px"
            />
          </ButtonContainer>        </ProfileSection>
        
        <SectionTitle>Phone Verification</SectionTitle>
        
        <FormContainer>
          <p style={{ marginBottom: '20px'}}>
            {isPhoneVerified 
              ? 'Your phone number is verified.' 
              : 'Your phone number is not verified. Please verify your phone number to enhance account security.'}
          </p>
          
          <Button 
            text={isPhoneVerified ? 'Verified' : 'Verify'} 
            onClick={isPhoneVerified ? () => {} : handleResendVerification} 
            loading={sendingVerification}
            disabled={isPhoneVerified || sendingVerification || resendCooldown > 0}
            bgColor={isPhoneVerified ? '#e0e0e0' : 'black'}
            textColor={isPhoneVerified ? '#757575' : '#fff'}
            width="auto"
            minWidth="200px"
          />
          
          {resendCooldown > 0 && 
            <CooldownText>Resend available in {resendCooldown} seconds</CooldownText>}
        </FormContainer>
      </ContentWrapper>
    </Container>
    </>
  );
};

export default ProfileScreen;